import React, { useContext, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import classnames from "classnames";
import { Context } from "./ModalDialogProvider";
import { Button, ButtonProps } from "@telia/teddy";
import FocusTrap from "../FocusTrap/FocusTrap";
import "./ModalDialog.less";

const KEY_ESC = 27;

interface ModalDialogProps {
    children?: React.ReactNode;
    className?: string;
    closeKind?: ButtonProps["variant"];
    closeText?: string;
    focusChildElement?: boolean;
    footerElement?: React.ReactElement;
    headerElement?: React.ReactElement;
    heading?: string;
    name: string;
    onClose?: (...args: any[]) => any;
    onSubmit?: (...args: any[]) => any;
    renderTo?: any;
    size?: "small" | "medium" | "large";
    standalone?: boolean;
    submitDisabled?: boolean;
    submitKind?: ButtonProps["variant"];
    submitText?: string;
}

const ModalDialog: React.FC<ModalDialogProps> = ({
    name,
    heading,
    children,
    onSubmit,
    submitDisabled,
    submitText,
    submitKind,
    onClose,
    closeText,
    closeKind,
    standalone,
    focusChildElement,
    headerElement,
    footerElement,
    size,
    className,
    ...rest
}) => {
    const modalNode = useContext(Context);
    const dialogRef = useRef<HTMLDivElement>(null);
    const dialogOverlayRef = useRef<HTMLDivElement>(null);

    const returnFocusOnDialogClose = () => {
        if (standalone) return;

        const lastFocusedElement: any = document.activeElement;

        return () => {
            if (lastFocusedElement) lastFocusedElement.focus();
        };
    };

    const setFocusOnDialog = () => {
        if (!dialogRef.current || standalone || focusChildElement) return;

        dialogRef.current.focus();
    };

    const disableAndResetPageScroll = () => {
        if (standalone) return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        if (dialogOverlayRef.current) {
            dialogOverlayRef.current.scrollTop = 0;
        }

        return () => {
            document.body.style.overflow = previousOverflow;
        };
    };

    const onOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target !== dialogOverlayRef.current) return;
        if (onClose) onClose();
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.keyCode === KEY_ESC && onClose) onClose();
    };

    const handleOverlayKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.keyCode === KEY_ESC && onClose) onClose();
    };

    useEffect(returnFocusOnDialogClose, []);
    useEffect(setFocusOnDialog, []);
    useEffect(disableAndResetPageScroll, []);

    const defaultHeaderElement = (
        <h2 id={`${name}-heading`} className="modalDialog__heading">
            {heading}
        </h2>
    );
    const defaultFooterElement = (
        <div className="modalDialog__footer">
            {closeText && onClose && (
                <Button className="modalDialog__footer__button" variant={closeKind} onClick={onClose}>
                    {closeText}
                </Button>
            )}
            {submitText && onSubmit && (
                <Button
                    className="modalDialog__footer__button"
                    variant={submitKind}
                    onClick={onSubmit}
                    disabled={submitDisabled}
                >
                    {submitText}
                </Button>
            )}
        </div>
    );

    const renderDialog = (Component: any, additionalProps = {}) => (
        <Component
            tabIndex={0}
            onKeyDown={handleKeyDown}
            ref={dialogRef}
            className={classnames(
                "modalDialog",
                size ? `container--${size}` : undefined,
                { modalDialog__standalone: standalone },
                className ? className : undefined,
            )}
            role="dialog"
            aria-labelledby={`${name}-heading`}
            aria-describedby={`${name}-description`}
            aria-modal={standalone ? undefined : true}
            {...rest}
            {...additionalProps}
        >
            {headerElement || defaultHeaderElement}
            <section id={`${name}-description`}>{children}</section>
            {footerElement || defaultFooterElement}
        </Component>
    );

    if (standalone) {
        return renderDialog("div");
    }

    if (!modalNode) return null;

    return (
        <>
            {createPortal(
                <>
                    <div
                        role="presentation"
                        ref={dialogOverlayRef}
                        onClick={onOverlayClick}
                        onKeyDown={handleOverlayKeyDown}
                        tabIndex={-1}
                        className="modalDialog__overlay"
                    >
                        {renderDialog(FocusTrap, { as: "div" })}
                    </div>
                </>,
                modalNode,
            )}
        </>
    );
};

export default ModalDialog;
